import { useCallback, useEffect, useRef } from 'react';

const INITIAL_SCROLL_POSITION = 0;
const DEFAULT_TOP_OFFSET_PX = 200;
const MESSAGE_TOP_OFFSET_PX = 10;

export const useScrollToUserMessage = ({
  messagesLength,
  onFinishScroll,
  onScrollTop,
  scrollConfig
}) => {
  const containerRef = useRef(null);
  const lastUserMessageRef = useRef(null);

  const previousScrollHeightRef = useRef(INITIAL_SCROLL_POSITION);
  const previousScrollOffsetRef = useRef(INITIAL_SCROLL_POSITION);
  const shouldRestoreAfterPrependRef = useRef(false);

  const isLoadingMoreRef = useRef(false);
  const ignoreScrollEventsRef = useRef(false);

  useEffect(() => {
    if (!scrollConfig.shouldScroll) return undefined;

    const reqAnimationFrameId = window.requestAnimationFrame(() => {
      const target = lastUserMessageRef.current;
      const container = containerRef.current;
      if (!target || !container) return;

      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const targetPosition =
        container.scrollTop + (targetTop - containerTop) - MESSAGE_TOP_OFFSET_PX;

      container.scrollTo({
        top: Math.max(targetPosition, INITIAL_SCROLL_POSITION),
        behavior: scrollConfig.behavior
      });

      onFinishScroll && onFinishScroll();
    });

    return () => {
      window.cancelAnimationFrame(reqAnimationFrameId);
    };
  }, [scrollConfig, onFinishScroll]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;

    if (!container || ignoreScrollEventsRef.current) return;

    const { scrollTop, scrollHeight } = container;
    const nearTop = scrollTop <= DEFAULT_TOP_OFFSET_PX;

    const canLoadMore = nearTop && onScrollTop && !isLoadingMoreRef.current;
    if (!canLoadMore) return;

    previousScrollOffsetRef.current = scrollHeight - scrollTop;
    previousScrollHeightRef.current = scrollHeight;
    shouldRestoreAfterPrependRef.current = true;

    isLoadingMoreRef.current = true;

    try {
      const maybePromise = onScrollTop();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.finally(() => {
          isLoadingMoreRef.current = false;
        });
      } else {
        isLoadingMoreRef.current = false;
      }
    } catch (e) {
      isLoadingMoreRef.current = false;
    }
  }, [onScrollTop]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!shouldRestoreAfterPrependRef.current) return;

    const { scrollHeight } = container;
    const heightDiff = scrollHeight - previousScrollHeightRef.current;

    if (heightDiff > 0) {
      const desiredTop = scrollHeight - previousScrollOffsetRef.current;

      ignoreScrollEventsRef.current = true;

      const prevBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = 'auto';
      container.scrollTop = desiredTop;
      container.style.scrollBehavior = prevBehavior;

      window.requestAnimationFrame(() => {
        ignoreScrollEventsRef.current = false;
      });
    }

    previousScrollHeightRef.current = scrollHeight;
    shouldRestoreAfterPrependRef.current = false;
  }, [messagesLength]);

  return { containerRef, lastUserMessageRef };
};
